import { createTheme } from '@mui/material/styles';

/**
 * Gerda design system — light theme.
 *
 * Single source of truth for the whole admin panel. To re-brand the app it is
 * enough to change the tokens below: every surface, border and accent in the UI
 * is derived from them.
 */
export const tokens = {
    // Accent — indigo/violet
    accent: '#6D5EF8',
    accentLight: '#A5A0FF',
    accentDark: '#4B3FD6',
    accentSoft: 'rgba(109, 94, 248, 0.08)',
    accentSofter: 'rgba(109, 94, 248, 0.04)',

    // Secondary — emerald
    mint: '#12B886',
    mintLight: '#3ECF9C',
    mintDark: '#0CA678',
    mintSoft: 'rgba(18, 184, 134, 0.10)',

    // Status
    success: '#12B886',
    warning: '#F59F00',
    error: '#F03E3E',
    info: '#4DABF7',

    // Surfaces
    canvas: '#F5F6FB',
    surface: '#FFFFFF',
    surfaceMuted: '#F7F8FC',
    sidebar: '#FFFFFF',

    // Ink
    ink: '#14161F',
    inkMuted: '#6A7183',
    inkFaint: '#9AA1B1',

    // Lines
    line: '#E8EAF1',
    lineStrong: '#DCDFEA',

    // Elevation
    shadowSm: '0 1px 2px rgba(20, 22, 31, 0.04)',
    shadowMd: '0 1px 2px rgba(20, 22, 31, 0.04), 0 8px 24px -12px rgba(20, 22, 31, 0.12)',
    shadowLg: '0 12px 32px -8px rgba(20, 22, 31, 0.16), 0 4px 12px -4px rgba(20, 22, 31, 0.08)',

    radius: 12,
};

export const fontFamily = [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"SF Pro Text"',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
].join(',');

let theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            light: tokens.accentLight,
            main: tokens.accent,
            dark: tokens.accentDark,
            contrastText: '#ffffff',
        },
        secondary: {
            light: tokens.mintLight,
            main: tokens.mint,
            dark: tokens.mintDark,
            contrastText: '#ffffff',
        },
        success: { main: tokens.success },
        warning: { main: tokens.warning },
        error: { main: tokens.error },
        info: { main: tokens.info },
        background: {
            default: tokens.canvas,
            paper: tokens.surface,
        },
        text: {
            primary: tokens.ink,
            secondary: tokens.inkMuted,
            disabled: tokens.inkFaint,
        },
        divider: tokens.line,
        action: {
            hover: 'rgba(20, 22, 31, 0.035)',
            selected: tokens.accentSoft,
        },
    },
    typography: {
        fontFamily,
        h5: {
            fontWeight: 600,
            fontSize: 22,
            letterSpacing: '-0.02em',
        },
        h6: {
            fontWeight: 600,
            fontSize: 17,
            letterSpacing: '-0.01em',
        },
        subtitle1: {
            fontWeight: 600,
            letterSpacing: '-0.01em',
        },
        body2: {
            fontSize: 14,
        },
        button: {
            fontWeight: 600,
            letterSpacing: 0,
            textTransform: 'none',
        },
        overline: {
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.08em',
        },
    },
    shape: {
        borderRadius: tokens.radius,
    },
    mixins: {
        toolbar: {
            minHeight: 48,
        },
    },
});

theme = {
    ...theme,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: tokens.canvas,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                '*::-webkit-scrollbar': {
                    width: 10,
                    height: 10,
                },
                '*::-webkit-scrollbar-thumb': {
                    backgroundColor: tokens.lineStrong,
                    borderRadius: 8,
                    border: '3px solid transparent',
                    backgroundClip: 'content-box',
                },
                '*::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: tokens.inkFaint,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    margin: '5px!important',
                    width: 'calc(100vw - 10px)',
                    maxHeight: 'calc(100vh - 64px)',
                    borderRadius: 16,
                    boxShadow: tokens.shadowLg,
                    [theme.breakpoints.down('md')]: {
                        //maxHeight: 'calc(100vh - 10px)',
                    },
                },
                paperFullScreen: {
                    margin: '0px!important',
                    width: '100vw',
                    maxWidth: '100vw',
                    height: '100vh',
                    maxHeight: '100vh',
                    borderRadius: 0,
                    [theme.breakpoints.down('md')]: {
                        maxHeight: 'calc(100vh - 64px)',
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: 18,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: tokens.sidebar,
                    borderRight: '1px solid ' + tokens.line,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    paddingLeft: 16,
                    paddingRight: 16,
                },
                sizeSmall: {
                    borderRadius: 8,
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    '&:active': {
                        boxShadow: 'none',
                    },
                },
                outlined: {
                    borderColor: tokens.lineStrong,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                scroller: {
                    overflowX: 'auto!important',
                },
                indicator: {
                    height: 3,
                    borderRadius: 3,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    letterSpacing: 0,
                    minHeight: 44,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: theme.spacing(1),
                    borderRadius: 10,
                    color: tokens.inkMuted,
                    transition: 'background-color .15s ease, color .15s ease',
                    '&:hover': {
                        color: tokens.accent,
                        backgroundColor: tokens.accentSoft,
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 8,
                    backgroundColor: 'rgba(20, 22, 31, 0.92)',
                    fontSize: 12,
                    fontWeight: 500,
                    padding: '6px 10px',
                },
                arrow: {
                    color: 'rgba(20, 22, 31, 0.92)',
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: tokens.line,
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    '&.Mui-selected': {
                        color: tokens.accent,
                        backgroundColor: tokens.accentSoft,
                    },
                    '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(109, 94, 248, 0.12)',
                    },
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: 14,
                    fontWeight: 500,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                    minWidth: 'auto',
                    marginRight: theme.spacing(1.75),
                    '& svg': {
                        fontSize: 20,
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    width: 32,
                    height: 32,
                    fontSize: 14,
                    fontWeight: 600,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid ' + tokens.line,
                },
                head: {
                    backgroundColor: tokens.surfaceMuted,
                    color: tokens.inkMuted,
                    fontWeight: 600,
                    fontSize: 12.5,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover > .MuiTableCell-body': {
                        backgroundColor: tokens.accentSofter,
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                elevation1: {
                    border: '1px solid ' + tokens.line,
                    boxShadow: tokens.shadowSm,
                },
                elevation2: {
                    border: '1px solid ' + tokens.line,
                    boxShadow: tokens.shadowMd,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.lineStrong,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: tokens.inkFaint,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.surfaceMuted,
                    border: '1px solid ' + tokens.line,
                    borderRadius: 10,
                    padding: 3,
                    gap: 3,
                },
                grouped: {
                    border: '0!important',
                    borderRadius: '8px!important',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13.5,
                    color: tokens.inkMuted,
                    padding: '5px 14px',
                    lineHeight: 1.5,
                    '&:hover': {
                        backgroundColor: 'rgba(20, 22, 31, 0.04)',
                    },
                    '&.Mui-selected': {
                        color: tokens.ink,
                        backgroundColor: tokens.surface,
                        boxShadow: tokens.shadowSm,
                        '&:hover': {
                            backgroundColor: tokens.surface,
                        },
                    },
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    borderRadius: '10px 10px 0 0',
                    backgroundColor: tokens.surfaceMuted,
                    '&:hover': {
                        backgroundColor: '#F1F2F8',
                    },
                    '&.Mui-focused': {
                        backgroundColor: tokens.surfaceMuted,
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    border: '1px solid ' + tokens.line,
                },
                list: {
                    padding: 6,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontSize: 14,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: 999,
                    height: 6,
                    backgroundColor: tokens.line,
                },
                bar: {
                    borderRadius: 999,
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-track': {
                        opacity: 1,
                        backgroundColor: tokens.lineStrong,
                    },
                },
            },
        },
    },
};

export default theme;
